/**
 * Какое сегодня число?
 * ====================================
 * TODO какой сегодня лунный день
 * TODO какой сегодня знак зодиака: луна в знаке зодиака
 */
import { LoaderFunctionArgs, MetaFunction } from "react-router";
import { useLoaderData, useLocation } from "react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import invariant from "tiny-invariant";

import H2 from "~/components/H2";
import AdditionalInfo from "~/components/tool-page/AdditionalInfo";
import AdditionalInfoSection from "~/components/tool-page/AdditionalInfoSection";
import ToolHeader from "~/components/tool-page/ToolHeader";
import { formatLongDate } from "~/helpers/dates";
import { makeTitle } from "~/helpers/pageTitle";
import { createWebAppSchema } from "~/helpers/schemaOrg";
import { getLocale } from "~/i18n";
import { getTranslator } from "~/i18next.server";
import CalculatorLayout from "~/routes/layouts/components/CalculatorLayout";
import ToolHeaderNew from "~/components/tool-page/ToolHeaderNew";
import AdditionalContentLayout from "~/routes/layouts/components/AdditionalContentLayout";

const CATEGORY = "dates";
const TKEY = "calculators.dates.today";
const ROUTE_ENDING = "today";
const LAST_UPDATED = new Date("2025-04-25T00:00:00.000Z");

export const meta: MetaFunction<typeof loader> = ({ data }) => [
  { title: makeTitle(data?.meta.title) },
  { name: "description", content: data?.meta.description },
  createWebAppSchema(
    data?.meta.locale as string,
    data?.meta.title as string,
    [],
    data?.meta.lastModified ? new Date(data.meta.lastModified) : null
  ),
];

export const handle = {
  localisedLinks: ["en", "ru", "de", "fr", "es", "it", "pt"]
};

// Format time in a consistent way for both server and client
function formatTimeForHydration(date: Date): string {
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  const seconds = date.getUTCSeconds().toString().padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const locale = getLocale(request.url) ?? "";

  const t = await getTranslator(request);

  // Calculate result if query is set

  const breadcrumbs = [
    { title: `catalog.categories.${CATEGORY}`, to: `${CATEGORY}` },
    { title: `${TKEY}.title`, to: `${CATEGORY}/${ROUTE_ENDING}` }
  ];

  // Create a fixed consistent date (12:00 noon UTC)
  // This ensures search engines always see the same content
  const today = new Date();
  const staticDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), 12, 0, 0, 0));

  // Pre-format time string for consistent hydration
  const staticTimeString = formatTimeForHydration(staticDate);

  return {
    // Pass the static date in a predictable format
    staticDate: staticDate.toISOString(),
    staticTimeString,
    staticTimezone: "UTC", // Static timezone for server rendering
    utcOffset: 0, // Default UTC offset for server rendering
    meta: {
      locale,
      title: t(`${TKEY}.title`),
      description: t(`${TKEY}.description`),
      breadcrumbs,
      lastModified: LAST_UPDATED.toISOString(),
    }
  };
};

export default function Index() {
  const {
    staticDate,
    staticTimeString,
    staticTimezone,
    utcOffset,
    meta
  } = useLoaderData<typeof loader>();

  const { t } = useTranslation("translations");
  const locale = getLocale(useLocation().pathname);
  invariant(locale);

  // Parse the static date for initial server render
  const parsedStaticDate = new Date(staticDate);
  
  // State for client-side rendering
  const [date, setDate] = useState(parsedStaticDate);
  const [timeString, setTimeString] = useState(staticTimeString);
  const [timezone, setTimezone] = useState(staticTimezone);
  const [timezoneOffset, setTimezoneOffset] = useState(utcOffset);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    // Mark that we're hydrated
    setHydrated(true);
    
    // After hydration, use the current time
    const currentDate = new Date();
    setDate(currentDate);
    setTimeString(currentDate.toLocaleTimeString(locale));
    setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    setTimezoneOffset(currentDate.getTimezoneOffset() / 60);
    
    // Update time every second (optional)
    const interval = setInterval(() => {
      const now = new Date();
      setDate(now);
      setTimeString(now.toLocaleTimeString(locale));
    }, 1000);

    return () => clearInterval(interval);
  }, [locale]);

  // Get display values (will be either static server-rendered or client values)
  const displayDate = hydrated ? date : parsedStaticDate;
  const displayTimezone = hydrated ? timezone : staticTimezone;
  
  // Format UTC offset string
  const sign = timezoneOffset > 0 ? "-" : "+";
  const absoluteOffset = Math.abs(timezoneOffset);

  return (
    <>
      <CalculatorLayout>
        <ToolHeaderNew
          name={meta.title}
          description={meta.description}
        />

        <div
          className="py-6 w-full flex flex-col md:gap-4 justify-center items-center font-extrabold uppercase text-slate-900 dark:text-white"
        >
          <div className="text-[1.7rem] md:text-[2.5rem] text-slate-600 dark:text-slate-100">
            {t(`${TKEY}.today_is`, { day: displayDate.toLocaleDateString(locale, {weekday: "long"}) })}
          </div>
          <div className="text-[2.5rem] md:text-[4rem] uppercase dark:text-yellow-300">
            {formatLongDate(displayDate, locale)}
          </div>
          <div className="text-[1.7rem] md:text-[2.5rem] uppercase text-slate-600 dark:text-slate-100">
            {timeString}
          </div>
        </div>

        <div
          className="mt-6 mb-10 w-full flex flex-col md:gap-4 justify-center items-center text-center font-extrabold uppercase text-slate-900 dark:text-white"
        >
          <div className="text-[1.5rem] md:text-[1.5rem] uppercase text-slate-500 dark:text-slate-100">
            {t(`${TKEY}.your_timezone_is`)} <span className="text-black dark:text-yellow-300">{displayTimezone}</span>,  
            UTC{sign}{absoluteOffset}
          </div>
        </div>
      </CalculatorLayout>

      <AdditionalContentLayout>
        <AdditionalInfo>
          <AdditionalInfoSection>
            <H2>{t(`${TKEY}.info_intro_h`)}</H2>
            <p>{t(`${TKEY}.info_intro_p`)}</p>
          </AdditionalInfoSection>
        </AdditionalInfo>
      </AdditionalContentLayout>
    </>
  );
}


